import {
  BarcodeIcon,
  CheckSquareOffsetIcon,
  ClipboardTextIcon,
  HouseIcon,
  ListIcon,
  FactoryIcon,
  WarehouseIcon,
  ReceiptIcon,
  GearFineIcon,
  ChartLineUpIcon,
  KanbanIcon,
  TreeStructureIcon,
  PuzzlePieceIcon,
  CubeTransparentIcon,
  FilesIcon,
  ProjectorScreenChartIcon,
  ShoppingCartIcon,
  TruckIcon,
  UserCircleIcon,
  StorefrontIcon,
  FileTextIcon,
  CheckSquareIcon,
  BellIcon,
  GearIcon,
  PrinterIcon,
} from '@phosphor-icons/react';

import { useLocation } from 'react-router';
import { useMemo } from 'react';
import { useSecureLayout } from '@hooks/useSecureLayout';
import { useSecureHeader } from '@contexts/secure-header';
import { useSidebar } from '@contexts/sidebar';
import { Badge, Button } from '@skupreme/shared/ui';
import { VersionPicker } from '@features/page-versions';

import { StyledHeader } from './styles';
import { Icon } from './Icon';
import { getPageInfo } from './utils';
import { IconSizes } from '@utility/constants/style-constants';

// Icon mapping for page info
const iconComponents: Record<string, React.ReactNode> = {
  CheckSquareOffset: <CheckSquareOffsetIcon size={IconSizes.small} />,
  ClipboardText: <ClipboardTextIcon size={IconSizes.small} />,
  House: <HouseIcon size={IconSizes.small} />,
  List: <ListIcon size={IconSizes.small} />,
  Factory: <FactoryIcon size={IconSizes.small} />,
  Warehouse: <WarehouseIcon size={IconSizes.small} />,
  Receipt: <ReceiptIcon size={IconSizes.small} />,
  GearFine: <GearFineIcon size={IconSizes.small} />,
  ChartLineUp: <ChartLineUpIcon size={IconSizes.small} />,
  Kanban: <KanbanIcon size={IconSizes.small} />,
  TreeStructure: <TreeStructureIcon size={IconSizes.small} />,
  PuzzlePiece: <PuzzlePieceIcon size={IconSizes.small} />,
  CubeTransparent: <CubeTransparentIcon size={IconSizes.small} />,
  Barcode: <BarcodeIcon size={IconSizes.small} />,
  Files: <FilesIcon size={IconSizes.small} />,
  ProjectorScreenChart: <ProjectorScreenChartIcon size={IconSizes.small} />,
  ShoppingCart: <ShoppingCartIcon size={IconSizes.small} />,
  Truck: <TruckIcon size={IconSizes.small} />,
  UserCircle: <UserCircleIcon size={IconSizes.small} />,
  Storefront: <StorefrontIcon size={IconSizes.small} />,
  FileText: <FileTextIcon size={IconSizes.small} />,
  CheckSquare: <CheckSquareIcon size={IconSizes.small} />,
  Printer: <PrinterIcon size={IconSizes.small} />,
  Gear: <GearIcon size={IconSizes.small} />,
  Bell: <BellIcon size={IconSizes.small} />,
};

function AppHeader() {
  const location = useLocation();
  const shouldShow = useSecureLayout();
  const { headerContent, headerAddon, headerActions } = useSecureHeader();
  const { isNavOpen, setIsNavOpen } = (useSidebar() ?? {}) as {
    isNavOpen?: boolean;
    setIsNavOpen?: (open: boolean) => void;
  };

  const pageInfo = useMemo(() => {
    const info = getPageInfo(location.pathname);
    return {
      icon: (
        <Icon>
          {iconComponents[info.iconName] || <ListIcon size={IconSizes.small} />}
        </Icon>
      ),
      title: info.title,
      tag: info.tag,
    };
  }, [location.pathname]);

  if (!shouldShow) {
    return null;
  }

  return (
    <StyledHeader>
      <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-3">
        {/* Below lg the sidebar is a drawer, and this opens it. */}
        <Button
          variant="ghost"
          shape="circle"
          aria-label="Open navigation"
          aria-expanded={!!isNavOpen}
          onPress={() => setIsNavOpen?.(true)}
          className="sk:-ml-1 sk:shrink-0 sk:lg:hidden"
        >
          <ListIcon size={IconSizes.medium} />
        </Button>
        {headerContent ?? (
          <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-2">
            {pageInfo.icon}
            <span className="sk:truncate sk:text-title sk:font-semibold sk:text-fg">
              {pageInfo.title}
            </span>
            {pageInfo.tag && <Badge tone="info">{pageInfo.tag}</Badge>}
          </div>
        )}
        {headerAddon}
      </div>
      <div className="sk:ml-auto sk:flex sk:shrink-0 sk:items-center sk:gap-2">
        {headerActions}
        <VersionPicker />
      </div>
    </StyledHeader>
  );
}

export default AppHeader;
